import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { Loader } from '@googlemaps/js-api-loader';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { promoterSchema } from 'utils/validationSchema';
import { useDispatch, useSelector } from 'react-redux';
import { useAlert } from 'context/AlertContext';
import { ClipLoader } from 'react-spinners';
import { register } from '../../../redux/actions/auth.js';
import {
  UserIcon,
  EnvelopeIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  GlobeAltIcon,
  MapPinIcon,
  LinkIcon,
  PhoneIcon,
  IdentificationIcon,
  BriefcaseIcon,
  ArrowLongRightIcon,
  PhotoIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

import ImageCropper from 'components/ImageCropper';
import { useImageCropper } from 'hooks/useImageCropper';
import {
  validateFiles,
  combinedUploadError,
  uploadErrorMessage,
  IMAGE_ACCEPT,
} from 'utils/uploadValidation';
import { Card, CardHeader, Field, FieldLabel } from '../primitives';
import { EASYPAY_CHOICE } from 'utils/easypayPayout';
import {
  PayoutJourney,
  EasypayConnectButton,
  EasypayCopy,
  HowYouGetPaidModal,
} from 'components/EasypayPayout';

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const getComponent = (components, type) =>
  components?.find((c) => c.types?.includes(type));

const extractCountry = (components) => {
  const c = getComponent(components, 'country');
  return { name: c?.long_name || '', code: c?.short_name || '' };
};

const extractCity = (components) =>
  getComponent(components, 'locality')?.long_name ||
  getComponent(components, 'postal_town')?.long_name ||
  getComponent(components, 'administrative_area_level_2')?.long_name ||
  getComponent(components, 'administrative_area_level_1')?.long_name ||
  '';

const isCountryPrediction = (opt) => {
  const types = opt?.value?.types || opt?.types || [];
  return types.includes('country');
};

const isCityLikeTypes = (types = []) =>
  [
    'locality',
    'postal_town',
    'administrative_area_level_3',
    'administrative_area_level_2',
    'administrative_area_level_1',
  ].some((t) => types.includes(t));

/**
 * One of the two Easypay sentences from section 05 (T2B-423).
 *
 * `boxed` frames the affirmative one so it reads as the expected answer without
 * making the other look like a warning — the promoter is allowed to defer, and
 * a red-edged escape hatch would say otherwise.
 */
const PayoutChoice = ({ checked, onChange, title, body, boxed = false }) => (
  <label
    className={[
      'flex cursor-pointer items-start gap-3 rounded-[12px] p-3.5 transition',
      boxed ? 'border border-line hover:bg-paper-2/50' : 'hover:bg-paper-2/40',
      checked ? 'bg-brand-softer' : '',
    ].join(' ')}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="peer sr-only"
    />
    <span
      aria-hidden
      className={[
        'mt-0.5 grid h-[18px] w-[18px] shrink-0 place-items-center rounded-[5px] border transition',
        'peer-focus-visible:ring-2 peer-focus-visible:ring-ink/25 peer-focus-visible:ring-offset-2',
        checked ? 'border-ink bg-ink text-white' : 'border-line-strong bg-white',
      ].join(' ')}
    >
      {checked && <CheckIcon className="h-3 w-3" strokeWidth={3} />}
    </span>
    <span className="min-w-0">
      <span className="block text-[14px] font-semibold leading-snug text-ink-strong">
        {title}
      </span>
      {body && (
        <span className="mt-0.5 block text-[12.5px] leading-[1.45] text-muted">
          {body}
        </span>
      )}
    </span>
  </label>
);

export const PromoterForm = ({ onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [, setMapsReady] = useState(false);
  const [countryOption, setCountryOption] = useState(null);
  const [countryCode, setCountryCode] = useState('');
  const [cityOption, setCityOption] = useState(null);
  const [howPaidOpen, setHowPaidOpen] = useState(false);

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { showAlert } = useAlert();

  const { error: pendingRegisterError } = useSelector(
    (state) => state.auth || {}
  );

  const [pendingRegisterErrorAlert, setPendingRegisterErrorAlert] =
    useState(false);

  const {
    imagePreview,
    croppedImage: croppedAvatar,
    croppedImagePreview,
    isCropping,
    handleImageChange,
    handleCropSave,
    setIsCropping,
    resetImage,
  } = useImageCropper();

  const {
    register: formRegister,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(promoterSchema),
    defaultValues: {
      country: '',
      countryCode: '',
      city: '',
      cityPlaceId: '',
      locationCoords: null,
      easypayOnboardingChoice: '',
    },
  });

  const companyNameValue = watch('companyName') || '';
  const usernameValue = watch('username') || '';

  // T2B-423: which of the two Easypay sentences the promoter agreed to. Lives
  // in the form (not local state) so yup can require an answer and the value
  // rides along with the rest of the signup payload.
  const easypayChoice = watch('easypayOnboardingChoice') || '';
  const selectEasypayChoice = (value) =>
    setValue('easypayOnboardingChoice', easypayChoice === value ? '' : value, {
      shouldValidate: true,
    });

  // Initials for avatar fallback medallion
  const initials = (usernameValue || companyNameValue || '')
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  useEffect(() => {
    let cancelled = false;
    const setReady = () => {
      if (!cancelled) setMapsReady(true);
    };

    if (window.google?.maps?.places) {
      setReady();
      return () => {
        cancelled = true;
      };
    }

    if (!API_KEY) {
      console.warn('Missing Google Maps API key — skipping Google loader.');
      setReady();
      return () => {
        cancelled = true;
      };
    }

    if (!window.__gmapsPromise) {
      const loader = new Loader({
        apiKey: API_KEY,
        libraries: ['places'],
        id: 'gmaps-places',
        language: (i18n.language || 'en').split('-')[0],
      });
      window.__gmapsPromise = loader.load();
    }

    window.__gmapsPromise
      .then(() => {
        setReady();
      })
      .catch((err) => {
        console.error('Google Maps JS failed to load', err);
        setReady();
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCountryChange = async (place) => {
    if (!place) {
      setCountryOption(null);
      setCountryCode('');
      setCityOption(null);
      setValue('country', '', { shouldValidate: true });
      setValue('countryCode', '', { shouldValidate: false });
      setValue('city', '', { shouldValidate: true });
      setValue('cityPlaceId', '', { shouldValidate: false });
      setValue('locationCoords', null, { shouldValidate: false });
      return;
    }

    setCountryOption(place);
    setCityOption(null);
    setValue('city', '', { shouldValidate: true });
    setValue('cityPlaceId', '', { shouldValidate: false });
    setValue('locationCoords', null, { shouldValidate: false });
    setValue('country', place?.label || '', { shouldValidate: true });
    setCountryCode('');
    setValue('countryCode', '', { shouldValidate: false });

    if (!place?.value?.place_id || !API_KEY) return;

    try {
      const resp = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?place_id=${place.value.place_id}&key=${API_KEY}`
      );
      const data = await resp.json();
      const components = data?.results?.[0]?.address_components || [];
      const { name, code } = extractCountry(components);
      const finalCountry = name || place.label || '';
      const finalCode = (code || '').toUpperCase();
      setValue('country', finalCountry, { shouldValidate: true });
      setCountryCode(finalCode);
      setValue('countryCode', finalCode, { shouldValidate: false });
    } catch (e) {
      console.error('Country geocode error:', e);
    }
  };

  const onCityChange = async (place) => {
    if (!place) {
      setCityOption(null);
      setValue('city', '', { shouldValidate: true });
      setValue('cityPlaceId', '', { shouldValidate: false });
      setValue('locationCoords', null, { shouldValidate: false });
      return;
    }

    setCityOption(place);
    const placeId = place?.value?.place_id || '';
    setValue('cityPlaceId', placeId, { shouldValidate: false });

    if (!placeId || !API_KEY) {
      const fallback = (place?.label || '').split(',')[0].trim();
      setValue('city', fallback, { shouldValidate: true });
      setCityOption({ ...place, label: fallback });
      return;
    }

    try {
      const resp = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?place_id=${placeId}&key=${API_KEY}`
      );
      const data = await resp.json();
      const result = data?.results?.[0];
      const components = result?.address_components || [];
      const cityName =
        extractCity(components) || (place?.label || '').split(',')[0].trim();
      setValue('city', cityName, { shouldValidate: true });
      setCityOption({ ...place, label: cityName });
      const coords = result?.geometry?.location;
      if (typeof coords?.lat === 'number' && typeof coords?.lng === 'number') {
        setValue(
          'locationCoords',
          { lat: coords.lat, lng: coords.lng },
          { shouldValidate: false }
        );
      } else {
        setValue('locationCoords', null, { shouldValidate: false });
      }
    } catch (e) {
      console.error('City geocode error:', e);
      const fallback = (place?.label || '').split(',')[0].trim();
      setValue('city', fallback, { shouldValidate: true });
      setCityOption({ ...place, label: fallback });
    }
  };

  const selectStyles = (isDisabled = false) => ({
    container: (base) => ({ ...base, width: '100%' }),
    control: (base) => ({
      ...base,
      minHeight: 48,
      height: 48,
      backgroundColor: isDisabled ? '#F4F2EC' : '#ffffff',
      borderColor: '#E1DDD2',
      borderRadius: 10,
      boxShadow: 'none',
      paddingLeft: 32,
      outline: 'none',
      '&:hover': { borderColor: '#E1DDD2' },
      cursor: isDisabled ? 'not-allowed' : 'default',
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
    placeholder: (base) => ({
      ...base,
      color: '#8A8579',
      fontSize: '14.5px',
    }),
    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: '14.5px' }),
    singleValue: (base) => ({ ...base, color: '#1A1A1F', fontSize: '14.5px' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({
      ...base,
      paddingRight: 12,
      paddingLeft: 8,
    }),
    clearIndicator: (base) => ({ ...base, paddingRight: 8, paddingLeft: 8 }),
    menu: (base) => ({
      ...base,
      zIndex: 80,
      borderRadius: 10,
      overflow: 'hidden',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? '#FAF9F6' : '#FFFFFF',
      color: '#1A1A1F',
      cursor: 'pointer',
      fontSize: '14.5px',
    }),
  });

  const placesAvailable = !!window.google?.maps?.places;

  useEffect(() => {
    if (pendingRegisterError && !pendingRegisterErrorAlert) {
      showAlert({
        type: 'error',
        title: pendingRegisterError || t('alert_dialog.register.error_title'),
        message: t('alert_dialog.register.error_message'),
        primaryAction: { label: t('common.ok') },
      });
      setPendingRegisterErrorAlert(true);
    }
  }, [pendingRegisterError, pendingRegisterErrorAlert, showAlert, t]);

  const showUploadErrors = (uploadErrors) => {
    showAlert({
      type: 'warning',
      title: t('upload.error_title', { defaultValue: 'Upload issue' }),
      message: uploadErrors.join('\n'),
      primaryAction: { label: t('common.ok') },
    });
  };

  const handleAvatarChange = (e) => {
    const files = Array.from(e.target.files || []);
    const { accepted, errors: uploadErrors } = validateFiles(files, {
      kind: 'image',
      t,
    });
    if (uploadErrors.length) {
      showUploadErrors(uploadErrors);
      e.target.value = '';
      return;
    }
    if (accepted.length) handleImageChange(e);
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    const combinedError = combinedUploadError([croppedAvatar], t);
    if (combinedError) {
      setIsSubmitting(false);
      showUploadErrors([combinedError]);
      return;
    }

    const formData = new FormData();

    if (croppedAvatar) {
      if (
        typeof croppedAvatar === 'string' &&
        croppedAvatar.startsWith('data:image')
      ) {
        const response = await fetch(croppedAvatar);
        const blob = await response.blob();
        formData.append('avatar', blob, 'avatar.jpg');
      } else if (
        croppedAvatar instanceof File ||
        croppedAvatar instanceof Blob
      ) {
        formData.append('avatar', croppedAvatar, 'avatar.jpg');
      }
    }

    for (const key of Object.keys(data)) {
      const value = data[key];
      if (value === undefined || value === null) continue;
      if (
        typeof value === 'object' &&
        !(value instanceof Date) &&
        !(value instanceof File) &&
        !(value instanceof Blob)
      ) {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    }

    try {
      await dispatch(register('promoters', formData));
      if (onSuccess) onSuccess(data?.email || '');
    } catch (error) {
      console.error(
        'Error submitting promoter registration:',
        error?.response?.data?.message
      );
      const uploadMessage = uploadErrorMessage(error, t);
      showAlert({
        type: 'error',
        title:
          error?.response?.data?.message ||
          t('alert_dialog.register.error_title'),
        message: uploadMessage || t('alert_dialog.register.error_message'),
        primaryAction: { label: t('common.ok') },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const sections = [
    {
      id: 'account',
      num: '01',
      label: t('register.section_account', 'Account'),
    },
    {
      id: 'profile',
      num: '02',
      label: t('register.section_profile', 'Profile'),
    },
    {
      id: 'company',
      num: '03',
      label: t('register.section_company', 'Company'),
    },
    {
      id: 'contact',
      num: '04',
      label: t('register.section_contact', 'Contact'),
    },
    {
      id: 'payouts',
      num: '05',
      label: t('register.section_payouts', 'Payouts'),
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr] lg:gap-14">
      <aside className="hidden lg:block">
        <div className="sticky top-6 flex flex-col gap-0.5">
          <div className="px-2.5 py-2 font-display text-[11px] font-bold uppercase tracking-[0.14em] text-muted">
            {t('register.toc_label', 'On this page')}
          </div>
          {sections.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById(s.id);
                if (el) {
                  window.scrollTo({
                    top: el.getBoundingClientRect().top + window.scrollY - 80,
                    behavior: 'smooth',
                  });
                }
              }}
              className="flex items-center gap-2.5 rounded-lg border-l-2 border-transparent px-2.5 py-2 text-[13.5px] font-medium text-ink-2 hover:bg-white/60"
            >
              <span className="w-[18px] font-display text-[11px] font-bold text-muted">
                {s.num}
              </span>
              {s.label}
            </a>
          ))}
          <div className="mt-4 rounded-[10px] border border-line bg-white/50 p-3.5 text-xs leading-[1.45] text-muted">
            <strong className="mb-1 block font-semibold text-ink">
              {t('register.toc_save_title', 'Auto-saving as you type')}
            </strong>
            {t('register.toc_save_body', 'You can leave and come back.')}
          </div>
        </div>
      </aside>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-6"
        noValidate
      >
        {/* 01 Account */}
        <Card id="account">
          <CardHeader
            num="01"
            title={t('register.section_account', 'Account')}
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field
              label={t('register.username_label', 'Username')}
              icon={
                <UserIcon className="h-[18px] w-[18px]" strokeWidth={1.7} />
              }
              error={errors.username?.message}
            >
              <input
                type="text"
                {...formRegister('username')}
                placeholder={t('register.username')}
              />
            </Field>
            <Field
              label={t('register.email_label', 'Email')}
              icon={
                <EnvelopeIcon className="h-[18px] w-[18px]" strokeWidth={1.7} />
              }
              error={errors.email?.message}
            >
              <input
                type="email"
                {...formRegister('email')}
                placeholder={t('register.email')}
              />
            </Field>
            <Field
              label={t('register.password_label', 'Password')}
              icon={
                <LockClosedIcon
                  className="h-[18px] w-[18px]"
                  strokeWidth={1.7}
                />
              }
              suffix={{
                icon: showPassword ? (
                  <EyeSlashIcon className="h-4 w-4" />
                ) : (
                  <EyeIcon className="h-4 w-4" />
                ),
                label: showPassword ? 'Hide password' : 'Show password',
                onClick: () => setShowPassword((s) => !s),
              }}
              error={errors.password?.message}
            >
              <input
                type={showPassword ? 'text' : 'password'}
                {...formRegister('password')}
                placeholder={t('register.password')}
              />
            </Field>
            <Field
              label={t('register.confirm_password_label', 'Confirm password')}
              icon={
                <LockClosedIcon
                  className="h-[18px] w-[18px]"
                  strokeWidth={1.7}
                />
              }
              suffix={{
                icon: showConfirmPassword ? (
                  <EyeSlashIcon className="h-4 w-4" />
                ) : (
                  <EyeIcon className="h-4 w-4" />
                ),
                label: showConfirmPassword ? 'Hide password' : 'Show password',
                onClick: () => setShowConfirmPassword((s) => !s),
              }}
              error={errors.confirmPassword?.message}
            >
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                {...formRegister('confirmPassword')}
                placeholder={t('register.confirm_password')}
              />
            </Field>
          </div>
        </Card>

        {/* 02 Profile */}
        <Card id="profile">
          <CardHeader
            num="02"
            title={t('register.section_profile', 'Profile')}
          />
          <input
            type="file"
            id="promoter-avatar-upload"
            accept={IMAGE_ACCEPT}
            className="sr-only"
            onChange={handleAvatarChange}
          />
          <div className="flex items-center gap-5">
            {croppedAvatar ? (
              <img
                src={croppedImagePreview || imagePreview}
                alt="Avatar Preview"
                className="h-28 w-28 rounded-full border border-field-border object-cover"
              />
            ) : initials ? (
              <div
                className="grid h-28 w-28 place-items-center rounded-full border border-field-border font-display text-[36px] font-extrabold"
                style={{
                  background: 'linear-gradient(135deg,#FFE7B8,#FFBD59)',
                  color: '#4a3a16',
                }}
              >
                {initials}
              </div>
            ) : (
              <label
                htmlFor="promoter-avatar-upload"
                className="grid h-28 w-28 cursor-pointer place-items-center rounded-full border border-field-border bg-paper-2"
              >
                <PhotoIcon className="h-10 w-10 text-muted" strokeWidth={1.5} />
              </label>
            )}
            <div className="flex-1">
              <div className="mb-1 font-display text-[11px] font-bold uppercase tracking-[0.12em] text-muted">
                {t('register.profile_photo', 'Profile photo')}
              </div>
              <div className="mb-2 text-sm text-ink-2">
                {usernameValue || t('register.upload_avatar', 'Upload avatar')}
              </div>
              <div className="flex gap-2">
                <label
                  htmlFor="promoter-avatar-upload"
                  className="cursor-pointer rounded-[10px] border border-line-strong px-3.5 py-2 text-[13px] font-semibold text-ink hover:bg-paper-2"
                >
                  {t('register.replace', 'Replace')}
                </label>
                {croppedAvatar && (
                  <button
                    type="button"
                    onClick={() => {
                      resetImage();
                      const el = document.getElementById(
                        'promoter-avatar-upload'
                      );
                      if (el) el.value = '';
                    }}
                    className="rounded-[10px] px-3.5 py-2 text-[13px] font-semibold text-red-500 hover:bg-red-50"
                  >
                    {t('register.remove', 'Remove')}
                  </button>
                )}
              </div>
            </div>
          </div>
          {errors.avatar && (
            <p className="text-xs text-red-500">{errors.avatar.message}</p>
          )}
          {isCropping && imagePreview && (
            <ImageCropper
              imageSrc={imagePreview}
              aspect={1}
              shape="round"
              onCropSave={handleCropSave}
              onClose={() => setIsCropping(false)}
            />
          )}

          <div className="flex flex-col gap-[7px]">
            <FieldLabel>
              {t('register.about_events_label', 'About your events')}
            </FieldLabel>
            <div className="relative">
              <textarea
                {...formRegister('profileInfo')}
                rows={5}
                className="w-full resize-none appearance-none rounded-[10px] border border-field-border bg-white px-3.5 py-3 text-[14.5px] leading-[1.5] text-ink outline-none transition focus:border-ink"
                placeholder={t('register.profile_info')}
              />
            </div>
            {errors.profileInfo && (
              <p className="text-xs text-red-500">
                {errors.profileInfo.message}
              </p>
            )}
          </div>
        </Card>

        {/* 03 Company */}
        <Card id="company">
          <CardHeader
            num="03"
            title={t('register.section_company', 'Company')}
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field
              label={t('register.company_name_label', 'Company name / Sole Proprietor')}
              icon={
                <BriefcaseIcon
                  className="h-[18px] w-[18px]"
                  strokeWidth={1.7}
                />
              }
              error={errors.companyName?.message}
            >
              <input
                type="text"
                {...formRegister('companyName')}
                placeholder={t('register.company_name')}
              />
            </Field>
            <Field
              label={t('register.fiscal_number_label', 'Fiscal / VAT number')}
              icon={
                <IdentificationIcon
                  className="h-[18px] w-[18px]"
                  strokeWidth={1.7}
                />
              }
              error={errors.fiscalNumber?.message}
            >
              <input
                type="text"
                {...formRegister('fiscalNumber')}
                placeholder={t('register.fiscal_number')}
              />
            </Field>

            <div className="flex flex-col gap-[7px]">
              <FieldLabel>{t('register.country_label', 'Country')}</FieldLabel>
              <div className="relative">
                <GlobeAltIcon
                  className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-[18px] w-[18px] -translate-y-1/2 text-muted"
                  strokeWidth={1.7}
                />
                {placesAvailable ? (
                  <GooglePlacesAutocomplete
                    autocompletionRequest={{ types: ['(regions)'] }}
                    selectProps={{
                      noOptionsMessage: () => t('homepage.no_options'),
                      value: countryOption,
                      onChange: onCountryChange,
                      placeholder: t('register.country'),
                      isClearable: true,
                      styles: selectStyles(false),
                      filterOption: (option) =>
                        isCountryPrediction(option?.data || option),
                    }}
                  />
                ) : (
                  <input
                    disabled
                    placeholder={t('register.country')}
                    className="w-full appearance-none rounded-[10px] border border-field-border bg-paper-2 px-3.5 py-3 pl-11 text-[14.5px] text-ink outline-none"
                  />
                )}
              </div>
              <input type="hidden" {...formRegister('country')} />
              {errors.country && (
                <p className="text-xs text-red-500">{errors.country.message}</p>
              )}
            </div>

            <div className="flex flex-col gap-[7px]">
              <FieldLabel>{t('register.city_label', 'City')}</FieldLabel>
              <div className="relative">
                <MapPinIcon
                  className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-[18px] w-[18px] -translate-y-1/2 text-muted"
                  strokeWidth={1.7}
                />
                {placesAvailable ? (
                  <GooglePlacesAutocomplete
                    autocompletionRequest={{
                      types: ['(cities)'],
                      ...(countryCode
                        ? { componentRestrictions: { country: countryCode } }
                        : {}),
                    }}
                    selectProps={{
                      value: cityOption,
                      onChange: onCityChange,
                      noOptionsMessage: () => t('homepage.no_options'),
                      placeholder: countryOption
                        ? t('register.city')
                        : t('register.select_country_first'),
                      isDisabled: !countryOption,
                      isClearable: true,
                      styles: selectStyles(!countryOption),
                      filterOption: (option) => {
                        const o = option?.data || option;
                        const types = o?.value?.types || o?.types || [];
                        return isCityLikeTypes(types);
                      },
                    }}
                  />
                ) : (
                  <input
                    disabled
                    placeholder={t('register.select_country_first')}
                    className="w-full appearance-none rounded-[10px] border border-field-border bg-paper-2 px-3.5 py-3 pl-11 text-[14.5px] text-ink outline-none"
                  />
                )}
              </div>
              <input type="hidden" {...formRegister('city')} />
              <input type="hidden" {...formRegister('countryCode')} />
              <input type="hidden" {...formRegister('cityPlaceId')} />
              <input type="hidden" {...formRegister('locationCoords')} />
              {errors.city && (
                <p className="text-xs text-red-500">{errors.city.message}</p>
              )}
            </div>
          </div>
        </Card>

        {/* 04 Contact */}
        <Card id="contact">
          <CardHeader
            num="04"
            title={t('register.section_contact', 'Contact')}
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field
              label={t('register.phone_label', 'Phone number')}
              icon={
                <PhoneIcon className="h-[18px] w-[18px]" strokeWidth={1.7} />
              }
              error={errors.phoneNumber?.message}
            >
              <input
                type="tel"
                {...formRegister('phoneNumber')}
                placeholder={t('register.phone_number')}
              />
            </Field>
            <Field
              label={t('register.website_label', 'Website')}
              optional={t('register.optional', 'optional')}
              icon={
                <LinkIcon className="h-[18px] w-[18px]" strokeWidth={1.7} />
              }
              error={errors.websiteUrl?.message}
            >
              <input
                type="text"
                {...formRegister('websiteUrl')}
                placeholder={t('register.website')}
              />
            </Field>
          </div>
        </Card>

        {/* 05 Payouts */}
        <Card id="payouts">
          <CardHeader
            num="05"
            title={t('register.section_payouts', 'Payouts')}
            badge={t('register.payouts_required_badge', 'Required')}
          />

          <div className="flex flex-col gap-5">
            <div>
              <EasypayCopy
                i18nKey="register.payouts_intro"
                className="m-0 max-w-[720px] text-[14px] leading-[1.55] text-ink-2"
              />
              <button
                type="button"
                onClick={() => setHowPaidOpen(true)}
                className="mt-2 text-[14px] font-semibold text-ink underline underline-offset-4 hover:text-brand-deep"
              >
                {t('easypay.how_paid_link', 'See how you get paid')}
              </button>
            </div>

            <PayoutJourney />

            <EasypayConnectButton />

            <div className="h-px bg-line" />

            {/* One answer, two boxes. They contradict each other, so ticking one
                clears the other — but they stay checkboxes rather than radios
                because each is a sentence the promoter is agreeing to, not an
                option in a list, and the second one is an acknowledgement of
                consequences. `role="radio"` would read them out as
                interchangeable choices, which they aren't. */}
            <fieldset className="m-0 flex flex-col gap-2.5 border-0 p-0">
              <legend className="sr-only">
                {t('register.payouts_choice_legend', 'Easypay account')}
              </legend>

              <PayoutChoice
                checked={easypayChoice === EASYPAY_CHOICE.DECLARED}
                onChange={() => selectEasypayChoice(EASYPAY_CHOICE.DECLARED)}
                title={t('register.payouts_choice_declared_title')}
                body={t('register.payouts_choice_declared_body')}
                boxed
              />

              <PayoutChoice
                checked={easypayChoice === EASYPAY_CHOICE.DEFERRED}
                onChange={() => selectEasypayChoice(EASYPAY_CHOICE.DEFERRED)}
                title={t('register.payouts_choice_deferred_title')}
              />

              {errors.easypayOnboardingChoice?.message && (
                <p className="m-0 text-xs text-red-500">
                  {errors.easypayOnboardingChoice.message}
                </p>
              )}
            </fieldset>
          </div>
        </Card>

        {/* Submit bar */}
        <div className="mt-2 flex flex-col gap-3.5 rounded-2xl bg-ink p-5 text-white lg:flex-row lg:items-center lg:justify-between lg:p-8">
          <div>
            <div className="text-[17px] font-extrabold leading-[1.25] tracking-[-0.01em] lg:text-[20px]">
              {t('register.submit_promoter_title', 'Ready to create events?')
                .split(/<em>(.*?)<\/em>/)
                .map((part, i) =>
                  i % 2 === 1 ? (
                    <em
                      key={i}
                      className="italic font-serif font-medium text-brand"
                    >
                      {part}
                    </em>
                  ) : (
                    <span key={i}>{part}</span>
                  )
                )}
            </div>
            <span className="mt-1.5 block text-[11.5px] text-white/55">
              {t(
                'register.submit_terms',
                'By creating an account you agree to our Terms and Privacy Policy.'
              )
                .split(/<a>(.*?)<\/a>/)
                .map((part, i) =>
                  i % 2 === 1 ? (
                    <a
                      key={i}
                      href={i === 1 ? '/terms' : '/privacy'}
                      className="text-white/85 underline"
                    >
                      {part}
                    </a>
                  ) : (
                    <span key={i}>{part}</span>
                  )
                )}
            </span>
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center justify-center gap-2 rounded-[10px] border border-brand bg-brand px-5 py-3.5 text-[15px] font-semibold text-ink transition hover:bg-brand-hover disabled:opacity-70"
          >
            {isSubmitting ? (
              <ClipLoader color="#1A1A1F" size={18} />
            ) : (
              <>
                {t('register.submit_promoter_cta', 'Create promoter account')}
                <ArrowLongRightIcon className="h-4 w-4" strokeWidth={2} />
              </>
            )}
          </button>
        </div>
      </form>

      <HowYouGetPaidModal
        isOpen={howPaidOpen}
        onClose={() => setHowPaidOpen(false)}
      />
    </div>
  );
};

export default PromoterForm;
